import {
  HHAccordion,
  HHAccordionDetails,
  HHAccordionSummary,
  HHButton,
  HHFormControl,
  HHFormControlLabel,
  HHLoadingButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { ExpandMore } from '@mui/icons-material';
import {
  Checkbox,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Theme,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { Controller, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import {
  PackageOfferingEnum,
  PackageType,
  ProgramIndicationType,
  useGetAllProductsQuery,
  useGetAllProgramIndicationsQuery,
} from '../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';
import {
  categorizeIndicationsForForm,
  IndicationCategory,
  isReadOnlyOffering,
} from '../../../utils/package-utils';
import {
  DEFAULT_PACKAGE_FORM_VALUES,
  PackageFormValues,
  PackageValidationSchema,
} from './validation-schema';

interface PackageFormProps {
  onSubmit: (values: PackageFormValues) => void | Promise<void>;
  initialValues?: PackageFormValues;
  packageId?: number;
  existingPackages?: PackageType[];
  isEditable?: boolean;
  readOnly?: boolean;
}

export const PACKAGE_FORM_NAME_TEST_ID = 'package-form-name-input';
export const PACKAGE_FORM_OFFERING_TYPE_TEST_ID =
  'package-form-offering-type-radio';
export const PACKAGE_FORM_INDICATIONS_TEST_ID =
  'package-form-indications-section';
export const PACKAGE_FORM_PRODUCT_TEST_ID = 'package-form-product-select';
export const PACKAGE_FORM_SUBMIT_BUTTON_TEST_ID = 'package-form-submit-button';
export const PACKAGE_FORM_CANCEL_BUTTON_TEST_ID = 'package-form-cancel-button';
export const PACKAGE_FORM_NOT_EDITABLE_TEST_ID =
  'package-form-not-editable-alert';
export const PACKAGE_FORM_CATEGORY_CHECKBOX_TEST_ID_PREFIX =
  'package-form-category-';

export const PackageForm = ({
  onSubmit,
  initialValues,
  packageId,
  existingPackages = [],
  isEditable = true,
  readOnly = false,
}: PackageFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();

  const { data: programIndicationsData, loading: indicationsLoading } =
    useGetAllProgramIndicationsQuery();
  const { data: productsData, loading: productsLoading } =
    useGetAllProductsQuery();

  const indications = [
    ...(programIndicationsData?.getProgramIndicationsById ?? []),
    {
      identifier: 'migraine',
      programName: 'migraine',
    } as ProgramIndicationType,
  ].sort((a, b) => a.identifier.localeCompare(b.identifier));
  const products = productsData?.getAllProducts ?? [];

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<PackageFormValues>({
    mode: 'onChange',
    resolver: yupResolver(PackageValidationSchema),
    defaultValues: initialValues ?? DEFAULT_PACKAGE_FORM_VALUES,
    context: {
      existingPackages,
      currentPackageId: packageId ?? null,
    },
    criteriaMode: 'all',
  });

  const watchedOfferingType = watch('offeringType');

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (values: PackageFormValues): Promise<void> => {
    await onSubmit(values);
  };

  const isEditMode = !!initialValues;

  return (
    <form>
      <HHStack spacing={6} direction="column">
        {!isEditable && (
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="warning.dark"
            data-testid={PACKAGE_FORM_NOT_EDITABLE_TEST_ID}
          >
            This package is linked to one or more contracts and cannot be
            edited.
          </HHTypography>
        )}

        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="outlined"
              label="Package Name"
              value={value}
              onChange={onChange}
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={!isEditable || readOnly}
              inputProps={{ role: 'textbox', name: 'name' }}
              data-testid={PACKAGE_FORM_NAME_TEST_ID}
            />
          )}
        />

        <Controller
          control={control}
          name="offeringType"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHFormControl data-testid={PACKAGE_FORM_OFFERING_TYPE_TEST_ID}>
              <HHTypography hhVariant="body2" color="text.secondary">
                Offering Type
              </HHTypography>
              <RadioGroup
                value={value}
                onChange={(e): void => {
                  const newType = e.target.value as PackageOfferingEnum;
                  onChange(newType);
                  if (newType === PackageOfferingEnum.ProgramIndication) {
                    setValue('productId', '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  } else {
                    setValue('programIndications', [], {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                }}
                row
              >
                <HHFormControlLabel
                  value={PackageOfferingEnum.ProgramIndication}
                  control={<Radio />}
                  label="Program"
                  disabled={!isEditable || readOnly}
                  data-testid="package-form-program-indication-radio"
                />
                <HHFormControlLabel
                  value={PackageOfferingEnum.Product}
                  control={<Radio />}
                  label="Product"
                  disabled={!isEditable || readOnly}
                  data-testid="package-form-product-radio"
                />
              </RadioGroup>
            </HHFormControl>
          )}
        />

        {watchedOfferingType === PackageOfferingEnum.ProgramIndication && (
          <Controller
            control={control}
            name="programIndications"
            render={({ field: { onChange, value } }): JSX.Element => {
              const categories = categorizeIndicationsForForm(indications);

              const renderCategoryItems = (
                category: IndicationCategory,
              ): JSX.Element => (
                <HHStack
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      category.name === 'Other' ? '1fr' : 'repeat(4, 1fr)',
                    gap: 0.5,
                  }}
                >
                  {category.items.map(pi => (
                    <HHFormControlLabel
                      key={pi.identifier}
                      control={
                        <Checkbox
                          checked={value.includes(pi.identifier)}
                          onChange={(e): void => {
                            const newValue = e.target.checked
                              ? [...value, pi.identifier]
                              : value.filter(v => v !== pi.identifier);
                            onChange(newValue);
                          }}
                          size="small"
                          disabled={
                            !isEditable || readOnly || isReadOnlyOffering(pi)
                          }
                        />
                      }
                      label={
                        <HHTypography hhVariant="body2">
                          {category.getDisplayLabel(pi)}
                        </HHTypography>
                      }
                      data-testid={`package-form-${pi.identifier}-checkbox`}
                    />
                  ))}
                </HHStack>
              );

              return (
                <HHStack
                  spacing={1}
                  data-testid={PACKAGE_FORM_INDICATIONS_TEST_ID}
                >
                  <HHTypography hhVariant="body2" color="text.secondary">
                    Package Offerings
                  </HHTypography>
                  {errors.programIndications?.message && (
                    <HHTypography
                      hhVariant="variant-bypass"
                      variant="caption"
                      color="error"
                    >
                      {errors.programIndications.message}
                    </HHTypography>
                  )}
                  {indicationsLoading ? (
                    <HHTypography hhVariant="body2">
                      Loading indications...
                    </HHTypography>
                  ) : (
                    categories.map(category => {
                      const mutableOfferings = category.items
                        .filter(pi => !isReadOnlyOffering(pi))
                        .map(pi => pi.identifier);

                      const mutableSelectedCount = mutableOfferings.filter(id =>
                        value.includes(id),
                      ).length;
                      const allMutableSelected =
                        mutableOfferings.length > 0 &&
                        mutableSelectedCount === mutableOfferings.length;
                      const someMutableSelected =
                        mutableSelectedCount > 0 && !allMutableSelected;
                      const isSingleOffering =
                        category.items.length === 1 &&
                        category.name !== 'Other';
                      const allReadOnly = mutableOfferings.length === 0;
                      const categoryTestId = `package-form-category-${category.name.toLowerCase().replace(/\s+/g, '-')}-checkbox`;

                      const handleSelectAllInCategory = (): void => {
                        const otherSelections = value.filter(
                          v => !mutableOfferings.includes(v),
                        );

                        onChange(
                          allMutableSelected
                            ? otherSelections
                            : [...otherSelections, ...mutableOfferings],
                        );
                      };

                      return (
                        <HHAccordion
                          key={category.name}
                          disableGutters
                          {...(isSingleOffering && { expanded: false })}
                          sx={(theme): SystemStyleObject<Theme> => ({
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            backgroundColor: theme.palette.background.paper,
                          })}
                        >
                          <HHAccordionSummary
                            {...(!isSingleOffering && {
                              expandIcon: <ExpandMore />,
                            })}
                            sx={{
                              padding: 0,
                              margin: 0,
                              minHeight: 'unset',
                              '&.Mui-expanded': { minHeight: 'unset' },
                              '& .MuiAccordionSummary-content': {
                                margin: '4px 0',
                                '&.Mui-expanded': { margin: '4px 0' },
                                ...(isSingleOffering && { cursor: 'default' }),
                              },
                              ...(isSingleOffering && {
                                cursor: 'default !important',
                              }),
                            }}
                          >
                            <HHFormControlLabel
                              control={
                                <Checkbox
                                  checked={allMutableSelected}
                                  indeterminate={someMutableSelected}
                                  onChange={handleSelectAllInCategory}
                                  onClick={(e): void => e.stopPropagation()}
                                  size="small"
                                  disabled={
                                    !isEditable || readOnly || allReadOnly
                                  }
                                />
                              }
                              label={
                                <HHTypography hhVariant="body2">
                                  {category.name}
                                </HHTypography>
                              }
                              onClick={(e): void => e.stopPropagation()}
                              data-testid={categoryTestId}
                            />
                          </HHAccordionSummary>
                          {!isSingleOffering && (
                            <HHAccordionDetails>
                              {renderCategoryItems(category)}
                            </HHAccordionDetails>
                          )}
                        </HHAccordion>
                      );
                    })
                  )}
                </HHStack>
              );
            }}
          />
        )}

        {watchedOfferingType === PackageOfferingEnum.Product && (
          <Controller
            control={control}
            name="productId"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHFormControl
                fullWidth
                error={!!errors.productId}
                data-testid={PACKAGE_FORM_PRODUCT_TEST_ID}
              >
                <InputLabel id="product-select-label">Product</InputLabel>
                <Select
                  labelId="product-select-label"
                  label="Product"
                  value={value}
                  onChange={(e): void => onChange(e.target.value)}
                  disabled={!isEditable || readOnly || productsLoading}
                >
                  {products.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {capitalizeFirstLetterAndSpaceSnakeString(p.name)}
                    </MenuItem>
                  ))}
                </Select>
                {errors.productId?.message && (
                  <HHTypography
                    hhVariant="variant-bypass"
                    variant="caption"
                    color="error"
                  >
                    {errors.productId.message}
                  </HHTypography>
                )}
              </HHFormControl>
            )}
          />
        )}

        {isEditable && !readOnly && (
          <HHStack direction="row" spacing={2} justifyContent="flex-end">
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
              data-testid={PACKAGE_FORM_CANCEL_BUTTON_TEST_ID}
            >
              Cancel
            </HHButton>
            <HHLoadingButton
              hhVariant="variant-bypass"
              variant="contained"
              type="button"
              disabled={!isEditable || !isValid || !isDirty || isSubmitting}
              loading={isSubmitting}
              onClick={handleSubmit(handleFormSubmit)}
              data-testid={PACKAGE_FORM_SUBMIT_BUTTON_TEST_ID}
            >
              {isEditMode ? 'Update' : 'Create'}
            </HHLoadingButton>
          </HHStack>
        )}
      </HHStack>
    </form>
  );
};
